/**
 * The payload the send-to-print mutation carries.
 *
 * This exists as its own module for one reason: the operator's identity was
 * being appended to an object literal inside a `useCallback`, and NOTHING in
 * this repository could catch its removal. Deleting `actorEmail` from that
 * spread left the unit suite green, produced no lint diagnostic (the name is
 * still referenced in the dependency array, so it does not read as unused),
 * and passed `tsc` — which checks nothing here, because `tsconfig.json` sets
 * `"include": []`. The mutation's own callback took an untyped `payload`, so
 * there was no typed relationship to `createSetupTicket` either.
 *
 * Assembling the payload here makes the invariant something a test can hold.
 */
export interface SendToPrintPayload {
  id?: string;
  customerId?: string;
  /**
   * REQUIRED here, though `createSetupTicket` accepts it optionally. At this
   * call site the operator is always resolvable, and requiring it is what
   * turns "someone dropped it from the spread" into a visible failure rather
   * than a silent loss of attribution on every job filed afterwards.
   */
  actorEmail: string;
  [key: string]: string | undefined;
}

/**
 * @param routeParams  `useParams()` — carries `year`, `month` and `customerId`.
 * @param generatedMagazineId  the generated magazine being sent to print.
 * @param actorEmail  the signed-in operator, from `useActorEmail`.
 */
export const buildSendToPrintPayload = (
  routeParams: Record<string, string | undefined>,
  generatedMagazineId: string | undefined,
  actorEmail: string
): SendToPrintPayload => ({
  ...routeParams,
  id: generatedMagazineId,
  actorEmail,
});
